const CURRENT_USER_OBJECT = localStorage.getItem("currentUser")
  ? JSON.parse(localStorage.getItem("currentUser"))
  : "";
const VATPERCENTAGE = CURRENT_USER_OBJECT[0]?.vatp;

export const defaultColumnsOrder = [
  "Slno",
  "Code",
  "Description",
  "Locn",
  "Unit",
  "Qty",
  "Unit Cost",
  "Unit BCCost",
  "Amount",
  "Oth.Cost",
  "x",
  "cntrl",
  "Frac",
  "VAT",
  "Vat%",
  "Vatamt",
  "MastInfo",
  "Pono",
  "Total",
  "OEM",
  "Group",
  "Disc%",
  "GIT",
  "Salesperson",
  "Bin",
  "Remarks",
  "DCP",
];

export const columnFieldMap = {
  Slno: "sino",
  Code: "code",
  Description: "description",
  Locn: "location",
  Unit: "unit",
  Qty: "qty",
  "Unit Cost": "unitCost",
  "Unit BCCost": "unitBCCost",
  Amount: "amount",
  "Oth.Cost": "othCost",
  x: "x",
  cntrl: "cntrl",
  Frac: "fraction",
  VAT: "vatEnabled",
  "Vat%": "vatPercent",
  Vatamt: "vatAmount",
  MastInfo: "mastInfo",
  Pono: "pono",
  Total: "total",
  OEM: "oem",
  Group: "group",
  "Disc%": "Discount%",
  GIT: "git",
  Salesperson: "salesperson",
  Bin: "bin",
  Remarks: "remarks",
  DCP: "dcp",
};

export const getBaseColumn = (
  field,
  widthMap,
  renderers,
  locationOptions,
  unitOptions,
  salespersonOptions
) => {
  const baseConfig = {
    field,
    headerName:
      Object.entries(columnFieldMap).find(
        ([, value]) => value === field
      )?.[0] || field,
    width: widthMap?.[field] || 100,
    sortable: false,
    filterable: false,
    disableColumnMenu: false,
  };

  if (field === "vatEnabled") {
    return {
      ...baseConfig,
      headerName: "VAT",
      width: widthMap?.[field] || 60,
      renderCell: (params) => renderers.renderVatToggle(params, field),
    };
  }

  // Numeric fields
  if (
    [
      "qty",
      "unitCost",
      "unitBCCost",
      "amount",
      "othCost",
      "x",
      "cntrl",
      "fraction",
      "vatPercent",
      "vatAmount",
      "total",
      "Discount%",
    ].includes(field)
  ) {
    return {
      ...baseConfig,
      renderCell: (params) =>
        renderers.renderEditableCell(params, field, "number"),
    };
  }

  // Text fields
  if (
    [
      "code",
      "description",
      "mastInfo",
      "pono",
      "oem",
      "group",
      "git",
      "bin",
      "remarks",
      "dcp",
    ].includes(field)
  ) {
    return {
      ...baseConfig,
      renderCell: (params) => renderers.renderEditableCell(params, field),
    };
  }

  if (field === "location") {
    return {
      ...baseConfig,
      renderCell: (params) =>
        renderers.renderSelectCell(params, "location", locationOptions),
    };
  }

  if (field === "unit") {
    return {
      ...baseConfig,
      renderCell: (params) =>
        renderers.renderSelectCell(params, "unit", unitOptions),
    };
  }
  if (field === "salesperson") {
    return {
      ...baseConfig,
      renderCell: (params) =>
        renderers.renderSelectCell(params, "salesperson", salespersonOptions),
    };
  }

  return baseConfig;
};

export const defaultColumnPercents = {
  sino: 5,
  code: 10,
  description: 20,
  location: 10,
  unit: 8,
  qty: 8,
  unitCost: 8,
  unitBCCost: 8,
  amount: 10,
  othCost: 8,
  "Discount%": 8,
  vatEnabled: 5,
  vatPercent: 8,
  vatAmount: 8,
  total: 10,
  actions: 5,
};

export const calculateRowValues = (
  item,
  purchaseData,
  context = {},
) => {
  const { items = [], otherCosts = [] } = context;

  const qty = Number(item.qty) || 0;
  const unitCost = Number(item.unitCost) || 0;
  const discountPercent = Number(item["Discount%"]) || 0;

  if (String(item.isActive || "").toUpperCase() === "NO") {
    const negativeAmount = -(qty * unitCost);
    return {
      ...item,
      amount: negativeAmount.toFixed(2),
      vatAmount: "0.00",
      vatPercent: 0,
      unitCost: unitCost,
      total: negativeAmount.toFixed(2),
    };
  }

  const documentVatPercent =
    purchaseData?.vatStatus === "VAT Applied"
      ? VATPERCENTAGE
      : 0;

  const lineVatEnabled = item.vatEnabled !== false;
  const vatPercent = lineVatEnabled ? documentVatPercent : 0;

  // Manual total edit
  if (
    item._manualTotalEdit &&
    item.total !== undefined &&
    item.total !== ""
  ) {
    const manualTotal = Number(item.total);

    const amount =
      vatPercent > 0
        ? manualTotal / (1 + vatPercent / 100)
        : manualTotal;

    const vatAmount = manualTotal - amount;

    const grossBeforeDiscount =
      amount + (amount * discountPercent) / 100;

    const newUnitCost =
      qty > 0 ? grossBeforeDiscount / qty : 0;

    return {
      ...item,
      amount: amount.toFixed(2),
      vatAmount: vatAmount.toFixed(2),
      vatPercent,
      unitCost: newUnitCost,
      total: manualTotal.toFixed(2),
      _manualTotalEdit: true,
    };
  }

  // Discount
  const lineDiscountPerUnit =
    unitCost * (discountPercent / 100);

  const unitCostAfterDiscount =
    unitCost - lineDiscountPerUnit;

  // Line Amount
  const amount =
    qty * unitCostAfterDiscount;

  // Other Cost Distribution
  const totalOtherCosts = otherCosts
    .filter(
      (oc) =>
        oc.drAcc?.trim() &&
        oc.crAcc?.trim(),
    )
    .reduce(
      (sum, oc) =>
        sum +
        (Number(
          oc.total || oc.amount,
        ) || 0),
      0,
    );

  const distributionBase = items.reduce(
    (sum, it) => {
      const q = Number(it.qty) || 0;
      const uc = Number(it.unitCost) || 0;
      const disc =
        Number(it["Discount%"]) || 0;

      const amountAfterDisc =
        q * (uc - (uc * disc) / 100);

      return sum + amountAfterDisc;
    },
    0,
  );

  let distributedOtherCost = 0;

  if (
    distributionBase > 0 &&
    totalOtherCosts > 0
  ) {
    distributedOtherCost =
      (amount / distributionBase) *
      totalOtherCosts;
  }

  const othCostPerUnit =
    qty > 0
      ? distributedOtherCost / qty
      : 0;

  // VAT
  const vatAmount =
    (amount * vatPercent) / 100;

  // Total
  const isImport = purchaseData?.purchaseType === "Import";
  const total =
    isImport ? amount : amount + vatAmount;

  return {
    ...item,
    amount: amount.toFixed(2),
    othCost: othCostPerUnit,
    vatPercent,
    vatAmount: vatAmount.toFixed(2),
    total: total.toFixed(2),
  };
};

export const getVatPercent = (purchaseData) => {
  return purchaseData?.vatStatus === "VAT Applied" ? VATPERCENTAGE : 0;
};

export const requiredFields = [
  "code",
  "description",
  "location",
  "unit",
  "qty",
  "unitCost",
];

export const newItemTemplate = (id, sino, defaultLocation) => ({
  id,
  sino,
  code: "",
  description: "",
  location: defaultLocation,
  unit: "",
  qty: 0,
  unitCost: 0,
  unitBCCost: 0,
  amount: 0,
  othCost: 0,
  x: 0,
  cntrl: 0,
  fraction: 0,
  vatEnabled: true, // Default VAT enabled (will use document VAT %)
  vatPercent: VATPERCENTAGE,
  vatAmount: 0,
  mastInfo: "",
  pono: "",
  total: 0,
  oem: "",
  group: "",
  "Discount%": 0,
  git: "",
  salesperson: "",
  bin: "",
  remarks: "",
  dcp: "",
  isIncluded: true,
});

const screenCode = "PURCHASEINVOICE";

export const purchaseConfig = {
  columnFieldMap,
  defaultColumnsOrder,
  getBaseColumn,
  calculateRowValues,
  getVatPercent,
  screenCode,
  defaultColumnPercents,
  requiredFields,
  newItemTemplate,
};
